import { useEffect, useState } from 'react';
import { X, Megaphone, AlertTriangle, Info, PartyPopper } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface Announcement {
  id: string;
  title: string;
  message: string;
  type: string;
}

const typeStyles: Record<string, { bg: string; icon: typeof Info }> = {
  info: { bg: 'bg-info/10 border-info/30 text-info', icon: Info },
  warning: { bg: 'bg-warning/10 border-warning/30 text-warning', icon: AlertTriangle },
  success: { bg: 'bg-success/10 border-success/30 text-success', icon: PartyPopper },
  promo: { bg: 'bg-primary/10 border-primary/30 text-primary', icon: Megaphone },
};

const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchAnnouncements = async () => {
      const { data } = await supabase
        .from('announcements')
        .select('id, title, message, type')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(3);
      if (data) setAnnouncements(data);
    };
    fetchAnnouncements();
  }, []);

  const dismiss = (id: string) => {
    setDismissed(prev => new Set(prev).add(id));
  };

  const visible = announcements.filter(a => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2">
      <AnimatePresence>
        {visible.map(a => {
          const style = typeStyles[a.type] || typeStyles.info;
          const Icon = style.icon;
          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={cn('rounded-xl border px-4 py-3 flex items-start gap-3', style.bg)}
            >
              <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold">{a.title}</p>
                <p className="text-[11px] opacity-80 mt-0.5">{a.message}</p>
              </div>
              <button onClick={() => dismiss(a.id)} className="p-0.5 rounded-full hover:bg-background/50 flex-shrink-0">
                <X className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default AnnouncementBanner;
